import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

export interface GenerateBiddingPoDialogData {
  invitation_id: number;
}

@Component({
  selector: 'app-generate-bidding-po-dialog',
  templateUrl: './generate-bidding-po-dialog.component.html',
  styleUrls: ['./generate-bidding-po-dialog.component.css']
})
export class GenerateBiddingPoDialogComponent implements OnInit {
  loading = true;
  submitting = false;
  error: string | null = null;
  invitation: any = null;
  ranks: Record<string, any> = {};
  rankOrder: string[] = [];
  items: any[] = [];
  existingPos: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<GenerateBiddingPoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: GenerateBiddingPoDialogData,
    private apiService: ApiService,
    private toastr: ToastrMsgService
  ) {}

  ngOnInit(): void {
    this.apiService.getBiddingPoPreview(this.data.invitation_id).subscribe({
      next: (res: any) => {
        this.loading = false;
        if (!res.success) {
          this.error = res.error || 'Failed to load PO preview';
          return;
        }
        this.invitation = res.data.invitation;
        this.ranks = res.data.ranks || {};
        this.rankOrder = ['L1', 'L2', 'L3'].filter((rank) => this.ranks[rank]);
        this.items = (res.data.items || []).map((item: any) => ({
          ...item,
          assigned_rank: item.assigned_rank || this.rankOrder[0] || 'L1'
        }));
        this.existingPos = res.data.existing_pos || [];
      },
      error: (err) => {
        this.loading = false;
        this.error = err.error?.error || 'Failed to load PO preview';
      }
    });
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);
  }

  quoteAmount(item: any, rank: string): number {
    return Number(item?.quotes?.[rank]?.amount) || 0;
  }

  vendorTotals(): Array<{ rank: string; org_name: string; total: number; count: number }> {
    return this.rankOrder.map((rank) => {
      const lines = this.items.filter((item) => item.assigned_rank === rank);
      return {
        rank,
        org_name: this.ranks[rank]?.org_name || rank,
        count: lines.length,
        total: lines.reduce((sum, item) => sum + this.quoteAmount(item, rank), 0)
      };
    }).filter((row) => row.count > 0);
  }

  onGenerate(): void {
    if (!this.items.length) {
      this.toastr.error('No items available to generate a PO');
      return;
    }
    this.submitting = true;
    this.apiService.generateBiddingPurchaseOrders(this.data.invitation_id, {
      items: this.items.map((item) => ({
        item_id: item.item_id,
        assigned_rank: item.assigned_rank
      }))
    }).subscribe({
      next: (res: any) => {
        this.submitting = false;
        if (res.success) {
          this.toastr.success(res.message || 'Purchase orders generated');
          this.dialogRef.close({ success: true, purchase_orders: res.data?.purchase_orders || [] });
        } else {
          this.toastr.error(res.error || 'Failed to generate purchase orders');
        }
      },
      error: (err) => {
        this.submitting = false;
        this.toastr.error(err.error?.error || 'Failed to generate purchase orders');
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
